<script setup lang="ts">
// v3.18.0 日志页 — 服务端运行日志 (node.log_file) 与审计日志 (哈希链文件)
// 查看/过滤/下载。用户需求: "包括所有日志"。
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { getServerLogs } from '@/api/tasks'

type Kind = 'runtime' | 'audit'
const kind = ref<Kind>('runtime')
const tail = ref(300)
const grep = ref('')
const appliedGrep = ref('')

const q = useQuery({
  queryKey: ['server-logs', kind, tail, appliedGrep],
  queryFn: () => getServerLogs(kind.value, tail.value, appliedGrep.value),
  refetchInterval: 3000,
})

const lines = computed<string[]>(() => q.data.value?.lines ?? [])
const file = computed<string>(() => q.data.value?.file ?? '')
const configured = computed<boolean>(() => q.data.value?.configured ?? true)

function applyFilter(): void {
  appliedGrep.value = grep.value.trim()
}

function switchKind(k: Kind): void {
  kind.value = k
  appliedGrep.value = ''
  grep.value = ''
}

function download(): void {
  const blob = new Blob([lines.value.join('\n')], { type: 'text/plain;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `afd-${kind.value}-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.log`
  a.click()
  URL.revokeObjectURL(a.href)
}

// 切 tab 时保留各自的过滤词 — 简化: 清空即可
watch(kind, () => {
  appliedGrep.value = ''
  grep.value = ''
})

function levelClass(line: string): string {
  if (line.includes('\tWARN\t') || line.includes('"level":"warn"') || line.includes('"severity":"warn"')) return 'text-amber-600'
  if (line.includes('\tERROR\t') || line.includes('"level":"error"') || line.includes('"decision":"deny"')) return 'text-red-600'
  return 'text-slate-600'
}
</script>

<template>
  <div class="p-6 max-w-6xl mx-auto">
    <div class="flex items-center gap-3 mb-4">
      <h2 class="text-lg font-bold text-slate-800 flex-none">日志</h2>
      <span class="modetab" :class="{ on: kind === 'runtime' }" @click="switchKind('runtime')">运行日志</span>
      <span class="modetab" :class="{ on: kind === 'audit' }" @click="switchKind('audit')">审计日志</span>
      <div class="flex-1"></div>
      <input
        v-model="grep"
        class="border border-slate-200 rounded-lg px-3 py-1.5 text-xs w-56"
        placeholder="包含过滤 (grep)"
        @keydown.enter="applyFilter"
      />
      <select v-model.number="tail" class="border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
        <option :value="100">尾部 100 行</option>
        <option :value="300">尾部 300 行</option>
        <option :value="1000">尾部 1000 行</option>
        <option :value="0">全部</option>
      </select>
      <button class="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white hover:bg-slate-50" @click="applyFilter">刷新</button>
      <button class="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white hover:bg-slate-50" @click="download">下载</button>
    </div>

    <div v-if="!configured" class="card text-sm text-slate-500">
      运行日志未配置落盘 — 在 config.yaml 的 <code class="font-mono">node.log_file</code> 设置路径后重启即可启用
      (例如 <code class="font-mono">/data/apps/afd/data/afd.log</code>)。审计日志不受此影响。
    </div>

    <div class="card">
      <div class="flex items-center gap-3 mb-2">
        <span class="text-xs text-slate-400 font-mono truncate flex-1">{{ file || '—' }}</span>
        <span class="text-xs text-slate-400">{{ lines.length }} 行 · 3s 自动刷新</span>
      </div>
      <div class="logbox font-mono text-[11px] leading-5">
        <div v-if="lines.length === 0" class="text-slate-400 py-2">无日志行 (或过滤无匹配)</div>
        <div v-for="(l, i) in lines" :key="i" class="whitespace-pre-wrap break-all" :class="levelClass(l)">{{ l }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modetab { font-size: 11.5px; padding: 4px 12px; border-radius: 7px; border: 1px solid #e2e8f0;
  background: #f8fafc; color: #64748b; cursor: pointer; user-select: none; }
.modetab.on { background: #6366f1; color: #fff; border-color: #6366f1; }
.card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 16px; }
.card > h3, .card .flex.items-center.gap-3 { }
.logbox { background: #0f172a; border-radius: 10px; padding: 10px 12px; max-height: 62vh;
  overflow-y: auto; }
.logbox div:hover { background: rgba(255,255,255,.04); }
</style>
